import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { notificationService } from "@/integrations/notifications/notificationService";
import { NotificationSettings, NotificationChannel } from "@/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  MessageCircle,
  AlertCircle,
  ChevronDown,
  Bell,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";

export const NotificationSection = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<NotificationSettings>({
    channels: {
      email: {
        enabled: false,
        format: "link_only",
      },
      telegram: {
        enabled: false,
        format: "link_only",
      },
    },
  });

  // Загрузка настроек при монтировании
  useEffect(() => {
    if (isExpanded) {
      loadSettings();
    }
  }, [isExpanded]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await notificationService.getNotificationSettings();
      setSettings(data);
      setError(null);
    } catch (err) {
      setError(t('profile.notifications.load_error'));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await notificationService.updateNotificationSettings(settings);
      addNotification({
        userId: user?.id || "",
        type: "success",
        message: t('profile.notifications.save_success'),
      });
      setError(null);
    } catch (err) {
      setError(t('profile.notifications.save_error'));
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const updateChannelSetting = (
    channel: "email" | "telegram",
    field: keyof NotificationChannel,
    value: boolean | string
  ) => {
    setSettings((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: {
          ...prev.channels[channel],
          [field]: value,
        },
      },
    }));
  };

  const renderChannelSettings = (
    channel: "email" | "telegram",
    title: string,
    icon: React.ReactNode,
    description: string
  ) => {
    const channelSettings = settings.channels[channel];

    return (
      <div className="mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <span className="text-base font-medium">{title}</span>
          </div>
          <Checkbox
            id={`${channel}-enabled`}
            checked={channelSettings.enabled}
            onCheckedChange={(checked) =>
              updateChannelSetting(channel, "enabled", Boolean(checked))
            }
          />
        </div>

        {channelSettings.enabled && (
          <div className="ml-7 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('profile.notifications.format_label')}</label>
              <RadioGroup
                value={channelSettings.format}
                onValueChange={(value) =>
                  updateChannelSetting(channel, "format", value)
                }
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="link_only"
                    id={`${channel}-format-link`}
                  />
                  <label htmlFor={`${channel}-format-link`}>
                    {t('profile.notifications.format_link')}
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="full_text"
                    id={`${channel}-format-text`}
                  />
                  <label htmlFor={`${channel}-format-text`}>
                    {channel === "email"
                      ? t('profile.notifications.format_full_text_email')
                      : t('profile.notifications.format_full_text_msg')}
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="with_attachment"
                    id={`${channel}-format-file`}
                  />
                  <label htmlFor={`${channel}-format-file`}>
                    {t('profile.notifications.format_attachment')}
                  </label>
                </div>
              </RadioGroup>
            </div>

            {channelSettings.format === "with_attachment" && (
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('profile.notifications.file_format_label')}</label>
                <Select
                  value={channelSettings.attachment_type || "pdf"}
                  onValueChange={(value) =>
                    updateChannelSetting(channel, "attachment_type", value)
                  }
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('profile.notifications.select_format')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="docx">DOCX</SelectItem>
                    <SelectItem value="html">HTML</SelectItem>
                    <SelectItem value="odt">ODT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              {channel === "email"
                ? `${t('profile.notifications.email_dest_info', { email: user?.email || t('profile.notifications.email_dest_not_set') })}`
                : t('profile.notifications.telegram_dest_info')}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-card border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-brand-100 text-brand-500 rounded-full flex items-center justify-center">
            <Bell size={20} />
          </div>
          <div>
            <h2 className="text-xl font-medium">{t('profile.notifications.title')}</h2>
            {!isExpanded && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('profile.notifications.subtitle')}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-10 w-10"
        >
          <ChevronDown
            size={20}
            className={`transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </Button>
      </div>

      {isExpanded && (
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="text-center py-4">{t('profile.notifications.loading')}</div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-6">
                {t('profile.notifications.description')}
              </p>

              {renderChannelSettings(
                "email",
                t('profile.notifications.email_channel_title'),
                <Mail className="h-5 w-5 text-brand-500" />,
                t('profile.notifications.email_channel_desc')
              )}

              {renderChannelSettings(
                "telegram",
                t('profile.notifications.telegram_channel_title'),
                <MessageCircle className="h-5 w-5 text-brand-500" />,
                t('profile.notifications.telegram_channel_desc')
              )}

              <div className="flex justify-end gap-4 mt-6">
                <Button onClick={handleSave} disabled={isLoading || isSaving}>
                  {isSaving ? t('profile.notifications.saving') : t('profile.notifications.save')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
